import { probeVideoDuration } from './video-duration';
import { canResume, planParts, progressPct, remainingParts, retryDelayMs } from './upload-plan';
import axios, { type AxiosInstance } from 'axios';
import type {
  ChapterUpsertInput,
  ThemeTokens,
  CourseAccessLevel,
  CreatorCourseCreateInput,
  CreatorCourseUpdateInput,
  CreatorProfileUpdateInput,
  CreatorRegisterInput,
  LessonUpsertInput,
} from '@mlm/shared';
import { useAuthStore, type AuthUser } from './auth-store';
import type { DailyPoint } from './series';
import { queryClient } from './query-client';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

export const api: AxiosInstance = axios.create({ baseURL: BASE_URL, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export { apiErrorMessage, apiErrorPayload } from './api-errors';

let refreshInFlight: Promise<string | null> | null = null;
async function refreshTokenOnce(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await axios.post<{ accessToken: string }>(
          `${BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true },
        );
        return res.data.accessToken;
      } catch {
        return null;
      } finally {
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      }
    })();
  }
  return refreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !String(original.url ?? '').includes('/auth/refresh') &&
      !String(original.url ?? '').includes('/auth/login') &&
      // A wrong-current-password 401 from the change-password form is a real
      // rejection, not an expired token — don't refresh+replay it (that burns
      // the account's 10/hr attempts, two per typo).
      !String(original.url ?? '').includes('/me/password')
    ) {
      original._retried = true;
      const newToken = await refreshTokenOnce();
      if (newToken) {
        const user = useAuthStore.getState().user;
        if (user) useAuthStore.getState().setSession(newToken, user);
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
      useAuthStore.getState().clear();
      queryClient.clear();
    }
    return Promise.reject(error);
  },
);

type MeResponse = {
  id: string;
  email: string;
  fullName: string | null;
  roles: string[];
  perms: string[];
  totpEnabled: boolean;
  // Optional on the wire so an older API (or a non-/me payload) still parses;
  // the store keeps it required and defaults to false.
  mustChangePassword?: boolean;
  // Only /me and the impersonation exchange emit this; /auth/login never does.
  impersonator?: { userId: string; email: string } | null;
};

export async function login(args: {
  email: string;
  password: string;
  totp?: string;
}): Promise<AuthUser> {
  const res = await api.post<{ accessToken: string; user: MeResponse }>('/auth/login', args);
  const u = res.data.user;
  const user: AuthUser = {
    id: u.id,
    email: u.email,
    fullName: u.fullName,
    roles: u.roles,
    perms: u.perms,
    totpEnabled: u.totpEnabled,
    mustChangePassword: u.mustChangePassword ?? false,
  };
  useAuthStore.getState().setSession(res.data.accessToken, user);
  return user;
}

/** Restore a session on app boot from the httpOnly refresh cookie. */
export async function restoreSession(): Promise<boolean> {
  const token = await refreshTokenOnce();
  if (!token) return false;
  try {
    const res = await api.get<MeResponse>('/me', { headers: { Authorization: `Bearer ${token}` } });
    const u = res.data;
    useAuthStore.getState().setSession(token, {
      id: u.id,
      email: u.email,
      fullName: u.fullName,
      roles: u.roles,
      perms: u.perms,
      totpEnabled: u.totpEnabled,
      mustChangePassword: u.mustChangePassword ?? false,
      // Carried through so a reloaded impersonation tab still shows the banner
      // — /auth/refresh re-signs the token from the refresh row, so the session
      // stays an impersonation even though the cookie name differs.
      impersonator: u.impersonator ?? null,
    });
    return true;
  } catch {
    return false;
  }
}

export async function logout(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } catch {
    /* ignore */
  }
  useAuthStore.getState().clear();
  queryClient.clear();
}

/**
 * Exchange a single-use grant token for an impersonation session. Called by
 * the /impersonate route after the admin SPA opened this tab; the response's
 * refresh cookie is scoped to THIS (creator) origin, so the admin's own
 * session on the admin origin is untouched.
 */
export async function exchangeImpersonationToken(token: string): Promise<AuthUser> {
  const res = await api.post<{ accessToken: string; user: MeResponse }>(
    '/auth/impersonation-exchange',
    { token },
  );
  const u = res.data.user;
  const user: AuthUser = {
    id: u.id,
    email: u.email,
    fullName: u.fullName,
    roles: u.roles,
    perms: u.perms,
    totpEnabled: u.totpEnabled,
    mustChangePassword: u.mustChangePassword ?? false,
    impersonator: u.impersonator ?? null,
  };
  useAuthStore.getState().setSession(res.data.accessToken, user);
  return user;
}

/**
 * End the current impersonation session — revokes the impersonation refresh
 * family and clears its cookie. The admin's own session is not touched.
 */
export async function endImpersonation(): Promise<void> {
  try {
    await api.post('/auth/end-impersonation');
  } catch {
    /* best-effort — the caller clears the in-memory session regardless */
  }
}

/** Change the signed-in user's own password (requires the current one). */
export async function changePassword(args: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  await api.post('/me/password', args);
}

/** Public tenant config — only the LMS master switch is read here. */
export async function getLmsEnabled(): Promise<boolean> {
  const res = await api.get<{ lmsEnabled?: boolean }>('/tenant/config');
  return res.data.lmsEnabled === true;
}

// ── domain types ──────────────────────────────────────────────────────────
export type CreatorStatus = 'pending' | 'approved' | 'rejected' | 'suspended';

export type RequirementDef = {
  key: string;
  label: string;
  description?: string;
  required: boolean;
  acceptsFile: boolean;
  fileTypes?: string[];
};

export type RequirementSubmission = {
  id: string;
  requirementKey: string;
  fileKey: string | null;
  textValue: string | null;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason: string | null;
};

export type CreatorMe = {
  /** Upload caps, so the editor can state them BEFORE the author picks a file. */
  limits: VideoLimits;
  creator: {
    id: string;
    userId: string;
    displayName: string;
    bio: string | null;
    avatarKey: string | null;
    links: { label: string; url: string }[];
    /** Null unless the admin opted to show it. */
    platformFeePct: string | null;
    showFeeToCreator: boolean;
    /** HOUSE account: the tenant's own staff. No wallet, no payouts, no review. */
    isHouse: boolean;
    status: CreatorStatus;
    rejectionReason: string | null;
    approvedAt: string | null;
    createdAt: string;
    ownerEmail: string;
    ownerName: string | null;
  };
  requirements: RequirementDef[];
  submissions: RequirementSubmission[];
};

export async function getRegistrationRequirements() {
  const res = await api.get<{ requirements: RequirementDef[] }>(
    '/creator/registration-requirements',
  );
  return res.data.requirements;
}

export async function registerCreator(input: CreatorRegisterInput) {
  const res = await api.post<{ userId: string; status: string }>('/creator/register', input);
  return res.data;
}

export async function getCreatorMe(): Promise<CreatorMe> {
  const res = await api.get<CreatorMe>('/creator/me');
  return res.data;
}

export async function updateCreatorProfile(input: CreatorProfileUpdateInput) {
  const res = await api.patch('/creator/me', input);
  return res.data;
}

export async function uploadAvatar(file: File) {
  const fd = new FormData();
  fd.append('file', file);
  await api.post('/creator/me/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
}

export async function submitTextRequirement(requirementKey: string, textValue: string) {
  const res = await api.post<RequirementSubmission>('/creator/requirements', {
    requirementKey,
    textValue,
  });
  return res.data;
}

export async function uploadFileRequirement(requirementKey: string, file: File) {
  const fd = new FormData();
  fd.append('requirementKey', requirementKey);
  fd.append('file', file);
  const res = await api.post<RequirementSubmission>('/creator/requirements/file', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

// ── course categories (topic registry, public list) ──────────────────────
export type CourseCategory = {
  id: string;
  name: string;
  sortOrder: number;
  active: boolean;
  /** Nesting is exactly one level deep, so a row with a parent is a leaf. */
  parentId: string | null;
};
export async function listCourseCategories() {
  const res = await api.get<CourseCategory[]>('/storefront/course-categories');
  return res.data;
}

// ── course tags (many-to-many taxonomy, public list) ──────────────────────
export type CourseTag = { id: string; name: string; sortOrder: number; active: boolean };
export async function listCourseTags() {
  const res = await api.get<CourseTag[]>('/storefront/course-tags');
  return res.data;
}

// ── courses ───────────────────────────────────────────────────────────────

/**
 * Content re-review state (Phase 8). 'clear' is also the state of a course
 * that has never been through content review at all, so it means "nothing
 * outstanding", not "an admin approved this".
 */
export type ContentReviewStatus = 'clear' | 'pending' | 'rejected';

/**
 * The tenant's policy for a course whose content is pending review. 'hold'
 * (the default) leaves the listing up and keeps STUDENTS on the last approved
 * version while the new one waits; 'flag' leaves the listing up and publishes
 * the change immediately; 'delist' pauses the listing. The creator SPA has no
 * features endpoint, so this rides the course payload.
 */
export type ContentReviewMode = 'flag' | 'delist' | 'hold';

/**
 * A sibling in a translation set. Drafts and unapproved listings are included
 * for the author's own editor — the public catalog filters them out, which is
 * why the editor has to explain the difference.
 */
export type CourseTranslation = {
  courseId: string;
  productId: string;
  name: string;
  language: string | null;
  status: 'draft' | 'published';
  approvalStatus: 'pending' | 'approved' | 'rejected';
};

export type CreatorCourseRow = {
  id: string;
  productId: string;
  name: string;
  price: string;
  srp: string | null;
  images: string[];
  active: boolean;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  rejectionReason: string | null;
  accessLevel: CourseAccessLevel;
  level: string | null;
  categoryId: string | null;
  /**
   * Content language code. The list route has always sent it; the translation
   * picker needs it to offer only courses in some OTHER language.
   */
  language: string | null;
  status: 'draft' | 'published';
  contentReviewStatus: ContentReviewStatus;
  contentReviewReason: string | null;
  /** Shared uuid of the translation set this course belongs to, or null. */
  translationGroupId: string | null;
  lessonCount: number;
  enrollmentCount: number;
  createdAt: string;
  updatedAt: string;
};

export type Lesson = {
  id: string;
  chapterId: string;
  courseId: string;
  title: string;
  description: string | null;
  sortOrder: number;
  sourceType: 'embed' | 'r2' | 'article';
  embedUrl: string | null;
  videoKey: string | null;
  videoStatus: 'none' | 'pending' | 'ready';
  /**
   * A STAGED upload, separate from the live video above (Phase 9). While these
   * are set the lesson keeps serving `videoKey` — the new file only takes over
   * once the server has verified it.
   */
  pendingVideoKey: string | null;
  pendingUploadId: string | null;
  pendingVideoBytes: number | null;
  pendingVideoAt: string | null;
  /** Article HTML (`sourceType==='article'`); sanitized server-side on save. */
  body: string | null;
  durationSeconds: number | null;
  isPreview: boolean;
  /** Drip: days after the learner's enrollment. null / 0 = immediately. */
  availableAfterDays: number | null;
  /** Drip: absolute unlock timestamp (ISO). Both set = the later one wins. */
  availableFrom: string | null;
  /**
   * `hold` mode: the source fields above are the STAGED version — what you
   * submitted and an admin has not approved yet. Students are still watching
   * `liveSourceType`'s content until they do.
   */
  heldPending?: boolean;
  liveSourceType?: 'embed' | 'r2' | 'article';
  /** WebVTT tracks on this lesson (R2 lessons only; embeds use their own). */
  captions?: LessonCaption[];
  /**
   * Downloadable attachments. Optional because the lesson POST/PATCH responses
   * are bare rows — only `getCourse` carries them, so every read is
   * `lesson.files ?? []` and the panel refetches the parent after a change.
   */
  files?: LessonFile[];
};

export type LessonCaption = { id: string; lang: string; label: string; sizeBytes: number };

export type LessonFile = {
  id: string;
  title: string;
  mimeType: string;
  sizeBytes: number;
  sortOrder: number;
  scanStatus: string;
  originalFilename: string | null;
};

export type Chapter = {
  id: string;
  courseId: string;
  title: string;
  sortOrder: number;
  lessons: Lesson[];
};

export type CourseDetail = {
  id: string;
  productId: string;
  productName: string;
  productActive: boolean;
  creatorUserId: string | null;
  accessLevel: CourseAccessLevel;
  subtitle: string | null;
  learnPoints: string[];
  requirements: string[];
  level: string | null;
  categoryId: string | null;
  /** Content language code ('en', 'fil', …) — see COURSE_LANGUAGES. */
  language: string | null;
  tags: { id: string; name: string }[];
  status: 'draft' | 'published';
  certificateTemplateId: string | null;
  enrollmentCount: number;
  contentReviewStatus: ContentReviewStatus;
  contentReviewReason: string | null;
  contentReviewMode: ContentReviewMode;
  translationGroupId: string | null;
  /** Siblings only — never this course itself. Empty when unlinked. */
  translations: CourseTranslation[];
  /** The public teaser, or null when the course has none. */
  promo: CoursePromo | null;
  chapters: Chapter[];
};

export async function listMyCourses() {
  const res = await api.get<CreatorCourseRow[]>('/creator/courses');
  return res.data;
}

export async function createCourse(input: CreatorCourseCreateInput) {
  const res = await api.post<{ id: string; productId: string }>('/creator/courses', input);
  return res.data;
}

export async function getCourse(id: string) {
  const res = await api.get<CourseDetail>(`/creator/courses/${id}`);
  return res.data;
}

export async function updateCourse(id: string, input: CreatorCourseUpdateInput) {
  const res = await api.patch<{ course: CourseDetail; approvalReset: boolean }>(
    `/creator/courses/${id}`,
    input,
  );
  return res.data;
}

/**
 * Join `targetCourseId`'s translation set (or start one with it). Catalog
 * metadata only: unlike a title or price edit this never re-opens listing
 * review, which is worth saying in the UI so nobody avoids the feature.
 *
 * The body key is `courseId` — the route's own `:id` is the course doing the
 * linking, the body names the course being linked to.
 */
export async function linkCourseTranslation(courseId: string, targetCourseId: string) {
  const res = await api.put<{ groupId: string }>(`/creator/courses/${courseId}/translation-link`, {
    courseId: targetCourseId,
  });
  return res.data;
}

/**
 * Remove the course NAMED IN THE URL from its translation set; the rest of the
 * set stays linked to each other. So dropping one sibling means passing that
 * sibling's id, and leaving the set entirely means passing my own.
 */
export async function unlinkCourseTranslation(courseId: string) {
  await api.delete(`/creator/courses/${courseId}/translation-link`);
}

export async function uploadCourseCover(courseId: string, file: File) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await api.post<{ images: string[] }>(`/creator/courses/${courseId}/images`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.images;
}

export async function removeCourseCover(courseId: string, key: string) {
  const res = await api.delete<{ images: string[] }>(`/creator/courses/${courseId}/images`, {
    data: { key },
  });
  return res.data.images;
}

/** Public catalog image URL (index-addressed, streamed by the API). */
export function productImageUrl(productId: string, idx = 0) {
  return `${BASE_URL}/products/${productId}/image/${idx}`;
}

export async function createChapter(courseId: string, input: ChapterUpsertInput) {
  const res = await api.post<Chapter>(`/creator/courses/${courseId}/chapters`, input);
  return res.data;
}
export async function updateChapter(id: string, input: Partial<ChapterUpsertInput>) {
  const res = await api.patch<Chapter>(`/creator/chapters/${id}`, input);
  return res.data;
}
export async function deleteChapter(id: string, opts: { force?: boolean } = {}) {
  await api.delete(`/creator/chapters/${id}`, {
    params: opts.force ? { force: 'true' } : undefined,
  });
}
export async function reorderChapters(courseId: string, ids: string[]) {
  await api.post(`/creator/courses/${courseId}/chapters/reorder`, { ids });
}

export async function createLesson(chapterId: string, input: LessonUpsertInput) {
  const res = await api.post<Lesson>(`/creator/chapters/${chapterId}/lessons`, input);
  return res.data;
}
export async function updateLesson(id: string, input: Partial<LessonUpsertInput>) {
  const res = await api.patch<Lesson>(`/creator/lessons/${id}`, input);
  return res.data;
}
/**
 * Play one of your own lessons — including a `hold`-mode staged version, which
 * is otherwise the one thing nobody can watch. Records no progress.
 */
export async function openLessonPreview(id: string) {
  const res = await api.post<LessonPreview>(`/creator/lessons/${id}/open`);
  return res.data;
}
export type LessonPreview = {
  lessonId: string;
  title: string;
  held: boolean;
} & (
  | { kind: 'video'; src: string; expiresInSeconds?: number }
  | { kind: 'iframe'; src: string }
  | { kind: 'article'; body: string }
);

export async function deleteLesson(id: string, opts: { force?: boolean } = {}) {
  await api.delete(`/creator/lessons/${id}`, {
    params: opts.force ? { force: 'true' } : undefined,
  });
}
export async function reorderLessons(chapterId: string, ids: string[]) {
  await api.post(`/creator/chapters/${chapterId}/lessons/reorder`, { ids });
}

/** Move a lesson into another chapter of the same course (it lands last). */
export async function moveLessonToChapter(lessonId: string, chapterId: string) {
  await api.post(`/creator/lessons/${lessonId}/move`, { chapterId });
}

/**
 * R2 video upload.
 *
 * Two paths behind one call. A small file is a single presigned PUT, as before.
 * Anything above one part size goes multipart: the file is sliced, parts upload
 * with a bounded concurrency and their own retry ladder, and the whole thing can
 * be cancelled or resumed. That matters here specifically — a 1 GB course video
 * over Philippine residential or mobile data is not a single reliable request,
 * and losing an hour of upload to one dropped packet is the failure the audit
 * called out.
 *
 * Progress is reported in BYTES, so the bar moves inside a part rather than
 * jumping between them.
 */
export type UploadHandle = {
  onProgress?: (pct: number, detail: { partsDone: number; partsTotal: number }) => void;
  signal?: AbortSignal;
};

export type VideoLimits = {
  maxBytes: number;
  partSize: number;
  singlePutMaxBytes: number;
  /** Hosts a lesson embed URL may point at; empty means the field is unset. */
  embedHosts?: string[];
};

export type VideoUploadStatus = {
  key: string | null;
  uploadId?: string | null;
  bytes?: number | null;
  partSize?: number;
  partCount?: number | null;
  uploadedParts?: number[];
};

/**
 * The API prefix an upload runs against.
 *
 * A lesson video and a course PROMO video are the same six endpoints on two
 * different rows (the server runs them through one `StagingTarget`), so the
 * client keeps ONE resume/retry/cancel state machine and varies the prefix. A
 * second copy is how one of them quietly loses the 403-re-sign branch.
 */
export const lessonVideoBase = (lessonId: string) => `/creator/lessons/${lessonId}/video`;
export const promoVideoBase = (courseId: string) => `/creator/courses/${courseId}/promo/video`;

/** What storage currently holds for this staged upload, if any. */
export async function getVideoUploadStatus(lessonId: string): Promise<VideoUploadStatus> {
  return getUploadStatus(lessonVideoBase(lessonId));
}

export async function getUploadStatus(base: string): Promise<VideoUploadStatus> {
  const res = await api.get<VideoUploadStatus>(`${base}/multipart/status`);
  return res.data;
}

export async function cancelLessonVideoUpload(lessonId: string, key: string, uploadId?: string) {
  await cancelUpload(lessonVideoBase(lessonId), key, uploadId);
}

export async function cancelUpload(base: string, key: string, uploadId?: string) {
  await api.post(`${base}/multipart/abort`, { key, uploadId });
}

/** Raised when the user cancels; callers suppress the toast for it. */
export class UploadCancelled extends Error {
  constructor() {
    super('video_upload_cancelled');
    this.name = 'UploadCancelled';
  }
}

async function putWithRetry(
  url: string,
  body: Blob,
  signal: AbortSignal | undefined,
  onBytes: (loaded: number) => void,
  resign: () => Promise<string>,
): Promise<void> {
  let attempt = 0;
  for (;;) {
    attempt += 1;
    try {
      await axios.put(url, body, {
        headers: { 'Content-Type': 'video/mp4' },
        withCredentials: false,
        signal,
        onUploadProgress: (e) => onBytes(e.loaded),
      });
      return;
    } catch (err) {
      if (signal?.aborted) throw new UploadCancelled();
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      // A 403 here is almost always an expired part URL (they last 6 hours and
      // a slow upload can outlive them), so re-sign once before counting it as
      // a failure.
      if (status === 403) {
        url = await resign();
        continue;
      }
      const delay = retryDelayMs(attempt);
      if (delay == null) throw err;
      await new Promise((r) => setTimeout(r, delay));
    }
  }
}

async function uploadMultipart(
  base: string,
  file: File,
  limits: VideoLimits,
  handle: UploadHandle,
  resume?: VideoUploadStatus,
): Promise<{ key: string; uploadId: string }> {
  let key: string;
  let uploadId: string;
  let partSize = limits.partSize;
  let uploadedParts: number[] = [];

  if (resume?.key && resume.uploadId && canResume(file.size, resume.bytes)) {
    key = resume.key;
    uploadId = resume.uploadId;
    partSize = resume.partSize ?? partSize;
    uploadedParts = resume.uploadedParts ?? [];
  } else {
    const start = await api.post<{
      key: string;
      uploadId: string;
      partSize: number;
      partCount: number;
      urls: { partNumber: number; url: string }[];
    }>(`${base}/multipart/start`, { contentLength: file.size });
    key = start.data.key;
    uploadId = start.data.uploadId;
    partSize = start.data.partSize;
  }

  const all = planParts(file.size, partSize);
  const todo = remainingParts(all, uploadedParts);
  const partsTotal = all.length;
  // Parts already in storage count towards the bar, or a resume would appear to
  // start over.
  const doneBytes = new Map<number, number>();
  for (const p of all) {
    if (!todo.includes(p)) doneBytes.set(p.partNumber, p.end - p.start);
  }
  const report = () =>
    handle.onProgress?.(
      progressPct(
        [...doneBytes.values()].reduce((a, b) => a + b, 0),
        file.size,
      ),
      { partsDone: doneBytes.size, partsTotal },
    );
  report();

  const signOne = async (partNumber: number): Promise<string> => {
    const res = await api.post<{ urls: { partNumber: number; url: string }[] }>(
      `${base}/multipart/sign-parts`,
      { key, uploadId, partNumbers: [partNumber] },
    );
    return res.data.urls[0]!.url;
  };

  // Three at a time: enough to use a decent connection, few enough that a phone
  // on mobile data is not fighting itself for bandwidth.
  const CONCURRENCY = 3;
  const queue = [...todo];
  const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
    for (;;) {
      const part = queue.shift();
      if (!part) return;
      if (handle.signal?.aborted) throw new UploadCancelled();
      const url = await signOne(part.partNumber);
      const size = part.end - part.start;
      await putWithRetry(
        url,
        file.slice(part.start, part.end),
        handle.signal,
        (loaded) => {
          doneBytes.set(part.partNumber, Math.min(loaded, size));
          report();
        },
        () => signOne(part.partNumber),
      );
      doneBytes.set(part.partNumber, size);
      report();
    }
  });
  await Promise.all(workers);
  return { key, uploadId };
}

export async function uploadVideo(
  base: string,
  file: File,
  limits: VideoLimits,
  handle: UploadHandle = {},
  resume?: VideoUploadStatus,
) {
  // Read the duration off the local file before it leaves the machine; the
  // server has no cheap way to learn it and an omitted value never clears one
  // the author typed.
  const durationSeconds = await probeVideoDuration(file);

  if (file.size <= limits.singlePutMaxBytes) {
    const pre = await api.post<{ url: string; key: string; maxBytes: number }>(
      `${base}/presign-upload`,
      { contentLength: file.size },
    );
    await axios.put(pre.data.url, file, {
      headers: { 'Content-Type': 'video/mp4' },
      withCredentials: false,
      signal: handle.signal,
      onUploadProgress: (e) => {
        if (e.total)
          handle.onProgress?.(progressPct(e.loaded, e.total), { partsDone: 0, partsTotal: 1 });
      },
    });
    if (handle.signal?.aborted) throw new UploadCancelled();
    const fin = await api.post<UploadResult>(`${base}/finalize`, {
      key: pre.data.key,
      ...(durationSeconds ? { durationSeconds } : {}),
    });
    return fin.data;
  }

  const { key, uploadId } = await uploadMultipart(base, file, limits, handle, resume);
  if (handle.signal?.aborted) throw new UploadCancelled();
  const fin = await api.post<UploadResult>(`${base}/multipart/complete`, {
    key,
    uploadId,
    ...(durationSeconds ? { durationSeconds } : {}),
  });
  return fin.data;
}

export type UploadResult = {
  sizeBytes: number;
  durationSeconds: number | null;
  /** `hold` mode staged it: students keep the approved video until it is approved. */
  held?: boolean;
};

export function uploadLessonVideo(
  lessonId: string,
  file: File,
  limits: VideoLimits,
  handle: UploadHandle = {},
  resume?: VideoUploadStatus,
) {
  return uploadVideo(lessonVideoBase(lessonId), file, limits, handle, resume);
}

export function uploadPromoVideo(
  courseId: string,
  file: File,
  limits: VideoLimits,
  handle: UploadHandle = {},
  resume?: VideoUploadStatus,
) {
  return uploadVideo(promoVideoBase(courseId), file, limits, handle, resume);
}

/** Point the course's promo at a hosted video (YouTube / Vimeo / direct file). */
export async function setPromoEmbed(courseId: string, embedUrl: string) {
  const res = await api.put<CoursePromo>(`/creator/courses/${courseId}/promo/embed`, { embedUrl });
  return res.data;
}

export async function deletePromo(courseId: string) {
  await api.delete(`/creator/courses/${courseId}/promo`);
}

export type CoursePromo = {
  sourceType: 'embed' | 'r2';
  embedUrl: string | null;
  videoStatus: 'none' | 'pending' | 'ready';
  durationSeconds: number | null;
  pendingVideoKey?: string | null;
};

/** Upload (or replace, per language) one WebVTT track on an R2 lesson. */
export async function uploadLessonCaption(
  lessonId: string,
  input: { file: File; lang: string; label: string },
) {
  const form = new FormData();
  form.append('lang', input.lang);
  form.append('label', input.label);
  form.append('file', input.file);
  const res = await api.post<LessonCaption>(`/creator/lessons/${lessonId}/captions`, form);
  return res.data;
}
export async function deleteLessonCaption(lessonId: string, captionId: string) {
  await api.delete(`/creator/lessons/${lessonId}/captions/${captionId}`);
}
/**
 * Add one downloadable attachment to a lesson (multipart, like the caption
 * upload). The response is the new row, but the editor refetches the course
 * instead of splicing it in — `files` lives on the lesson the parent holds.
 */
export async function uploadLessonFile(lessonId: string, input: { file: File; title: string }) {
  const form = new FormData();
  form.append('title', input.title);
  form.append('file', input.file);
  const res = await api.post<LessonFile>(`/creator/lessons/${lessonId}/files`, form);
  return res.data;
}

export async function deleteLessonFile(lessonId: string, fileId: string) {
  await api.delete(`/creator/lessons/${lessonId}/files/${fileId}`);
}

export async function clearLessonVideo(lessonId: string) {
  await api.delete(`/creator/lessons/${lessonId}/video`);
}

// ── stats ─────────────────────────────────────────────────────────────────
export type CourseStatsRow = {
  courseId: string;
  productId: string;
  title: string;
  status: string;
  accessLevel: string;
  enrollments: number;
  starters: number;
  completers: number;
  completionPct: number;
  lessonCount: number;
  views: number;
  /** Average of UNHIDDEN reviews, 1 dp; null when nobody has reviewed it. */
  ratingAvg: number | null;
  ratingCount: number;
};
// The daily series shape lives beside its zero-fill helper.
export type { DailyPoint };
export type Wallet = {
  available: string;
  pending: string;
  lifetimeEarned: string;
  updatedAt: string | null;
};

/**
 * The audience numbers the creator's PUBLIC page shows, from the same service —
 * so what an instructor reads on their dashboard is what a learner reads about
 * them. `subscriberCount` is null when the tenant does not sell per-creator
 * subscriptions at all.
 */
export type CreatorAudience = {
  followers: number;
  subscriberCount: number | null;
  studentCount: number;
  videoCount: number;
  rating: { average: number; count: number };
};

export async function getStatsOverview() {
  const res = await api.get<{
    courses: CourseStatsRow[];
    series: DailyPoint[];
    wallet: Wallet;
    audience: CreatorAudience | null;
  }>('/creator/stats/overview');
  return res.data;
}

export type LessonFunnelRow = {
  lessonId: string;
  chapterTitle: string;
  title: string;
  opened: number;
  completed: number;
  completionPct: number;
  opens: number;
};
export type TestFunnelRow = {
  testId: string;
  title: string;
  isPractice: boolean;
  chapterTitle: string | null;
  attempts: number;
  passed: number;
  pending: number;
  avgScorePct: number | null;
};
export async function getCourseStats(courseId: string) {
  const res = await api.get<{
    funnel: LessonFunnelRow[];
    series: DailyPoint[];
    tests: TestFunnelRow[];
  }>(`/creator/courses/${courseId}/stats`);
  return res.data;
}

export type EnrollmentRow = {
  userName: string | null;
  source: string;
  grantedAt: string;
  revokedAt: string | null;
  lessonsCompleted: number;
  lessonCount: number;
  progressPct: number;
  lastActivityAt: string | null;
  testsPassed: number;
  testsRequired: number;
  certificateIssuedAt: string | null;
};
export async function getCourseEnrollments(courseId: string) {
  const res = await api.get<EnrollmentRow[]>(`/creator/courses/${courseId}/enrollments`);
  return res.data;
}

// ── wallet + payouts ──────────────────────────────────────────────────────
export type LedgerEntry = {
  id: string;
  kind: string;
  amount: string;
  currency: string;
  direction: 'C' | 'D';
  metadata: Record<string, unknown>;
  createdAt: string;
  orderCode: string | null;
};

export async function getWallet() {
  const res = await api.get<Wallet>('/creator/wallet');
  return res.data;
}
export async function getLedger(limit = 50) {
  const res = await api.get<LedgerEntry[]>('/creator/wallet/ledger', { params: { limit } });
  return res.data;
}

export type PayoutMethod = {
  code: string;
  label: string;
  accountLabel: string;
  accountPlaceholder: string;
};
export type PayoutBreakdown = {
  gross: string;
  serviceCharge: string;
  adminFee: string;
  withholdingTax: string;
  net: string;
  steps?: { key: string; label: string; amount: string }[];
};
export type PayoutRequest = {
  id: string;
  amount: string;
  currency: string;
  method: string;
  serviceCharge: string;
  adminFee: string;
  withholdingTax: string;
  netAmount: string;
  status: string;
  requestedAt: string;
  processedAt: string | null;
  rejectionReason: string | null;
};

export async function getPayoutMethods() {
  const res = await api.get<{ methods: PayoutMethod[] }>('/creator/payouts/methods');
  return res.data.methods;
}
export async function previewPayout(amount: string, method: string) {
  const res = await api.post<PayoutBreakdown>('/creator/payouts/requests/preview', {
    amount,
    method,
  });
  return res.data;
}
export async function requestPayout(input: {
  amount: string;
  method: string;
  payload?: Record<string, unknown>;
}) {
  const res = await api.post<PayoutRequest>('/creator/payouts/requests', input);
  return res.data;
}
export async function listPayouts() {
  const res = await api.get<PayoutRequest[]>('/creator/payouts/requests');
  return res.data;
}
export async function cancelPayout(id: string) {
  const res = await api.post<PayoutRequest>(`/creator/payouts/requests/${id}/cancel`);
  return res.data;
}

// ── certificates ──────────────────────────────────────────────────────────
export type CertificatePlaceholder = {
  field: 'member_name' | 'course_title' | 'issued_date' | 'verify_code';
  xPct: number;
  yPct: number;
  widthPct: number;
  fontSizePx: number;
  color: string;
  align: 'left' | 'center' | 'right';
};
export type CertificateTemplate = {
  id: string;
  creatorUserId: string | null;
  name: string;
  imageKey: string;
  config: { placeholders: CertificatePlaceholder[] };
  createdAt: string;
  updatedAt: string;
};
export async function listTemplates() {
  const res = await api.get<CertificateTemplate[]>('/creator/certificate-templates');
  return res.data;
}
export async function listTemplatePicker() {
  const res = await api.get<{ id: string; name: string; creatorUserId: string | null }[]>(
    '/creator/certificate-templates/picker',
  );
  return res.data;
}
export async function createTemplate(
  name: string,
  file: File,
  config?: { placeholders: CertificatePlaceholder[] },
) {
  const fd = new FormData();
  fd.append('name', name);
  if (config) fd.append('config', JSON.stringify(config));
  fd.append('file', file);
  const res = await api.post<CertificateTemplate>('/creator/certificate-templates', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}
/**
 * A layout-only edit goes as JSON; replacing the background goes as multipart.
 * Same route either way — the server branches on `isMultipart()`.
 */
export async function updateTemplate(
  id: string,
  patch: { name?: string; config?: { placeholders: CertificatePlaceholder[] } },
  file?: File | null,
) {
  if (file) {
    const fd = new FormData();
    if (patch.name !== undefined) fd.append('name', patch.name);
    if (patch.config) fd.append('config', JSON.stringify(patch.config));
    fd.append('file', file);
    const res = await api.patch<CertificateTemplate>(`/creator/certificate-templates/${id}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  }
  const res = await api.patch<CertificateTemplate>(`/creator/certificate-templates/${id}`, patch);
  return res.data;
}
export async function deleteTemplate(id: string) {
  await api.delete(`/creator/certificate-templates/${id}`);
}
/**
 * PUBLIC route: an `<img>` cannot send the Authorization header, so the
 * previously permission-guarded creator route could never actually render.
 */
export function templateImageUrl(id: string) {
  return `${BASE_URL}/certificate-templates/${id}/image`;
}
export async function assignCourseTemplate(courseId: string, templateId: string | null) {
  await api.put(`/creator/courses/${courseId}/certificate-template`, { templateId });
}

// ── Q&A inbox ─────────────────────────────────────────────────────────────
export type InboxQuestion = {
  id: string;
  body: string;
  hidden: boolean;
  createdAt: string;
  authorName: string | null;
  lessonId: string;
  lessonTitle: string;
  courseId: string;
  replyCount: number;
  creatorReplied: boolean;
};
// ── course tests (the quiz gate before the certificate) ───────────────────
export type TestQuestion = {
  id: string;
  testId: string;
  kind: 'multiple_choice' | 'essay';
  prompt: string;
  choices: string[];
  correctIndex: number | null;
  /** Shown to a learner after a PRACTICE attempt; server-only on a graded test. */
  explanation: string | null;
  points: number;
  sortOrder: number;
};
export type CourseTest = {
  id: string;
  courseId: string;
  chapterId: string | null;
  title: string;
  description: string | null;
  passingScorePct: number;
  maxAttempts: number | null;
  active: boolean;
  /** Self-check: unlimited retakes, answers revealed, never gates a certificate. */
  isPractice: boolean;
  questions: TestQuestion[];
};
export async function listCourseTests(courseId: string) {
  const res = await api.get<CourseTest[]>(`/creator/courses/${courseId}/tests`);
  return res.data;
}
export type TestUpsert = {
  title: string;
  description?: string | null;
  chapterId?: string | null;
  passingScorePct?: number;
  maxAttempts?: number | null;
  active?: boolean;
  /** Create-time only, like `chapterId` — the PATCH type omits it. */
  isPractice?: boolean;
};
export async function createCourseTest(courseId: string, input: TestUpsert) {
  const res = await api.post<CourseTest>(`/creator/courses/${courseId}/tests`, input);
  return res.data;
}
export async function updateCourseTest(
  testId: string,
  patch: Omit<Partial<TestUpsert>, 'chapterId' | 'isPractice'>,
) {
  await api.patch(`/creator/course-tests/${testId}`, patch);
}
export async function deleteCourseTest(testId: string) {
  await api.delete(`/creator/course-tests/${testId}`);
}
export type TestQuestionUpsert = {
  kind: 'multiple_choice' | 'essay';
  prompt: string;
  choices?: string[];
  correctIndex?: number | null;
  explanation?: string | null;
  points?: number;
  sortOrder?: number;
};
export async function createTestQuestion(testId: string, input: TestQuestionUpsert) {
  const res = await api.post<TestQuestion>(`/creator/course-tests/${testId}/questions`, input);
  return res.data;
}
export async function updateTestQuestion(questionId: string, input: TestQuestionUpsert) {
  await api.patch(`/creator/test-questions/${questionId}`, input);
}
export async function deleteTestQuestion(questionId: string) {
  await api.delete(`/creator/test-questions/${questionId}`);
}
export async function reorderTestQuestions(testId: string, ids: string[]) {
  await api.post(`/creator/course-tests/${testId}/questions/reorder`, { ids });
}

// ── grading inbox (essay attempts awaiting review) ─────────────────────────
export type PendingAttemptRow = {
  id: string;
  testId: string;
  testTitle: string;
  courseId: string;
  courseName: string;
  userId: string;
  memberName: string | null;
  attemptNumber: number;
  autoScore: number;
  maxScore: number;
  submittedAt: string;
};
export async function listPendingAttempts() {
  const res = await api.get<PendingAttemptRow[]>('/creator/test-attempts');
  return res.data;
}
export type AttemptEntry = {
  questionId: string;
  kind: 'multiple_choice' | 'essay';
  prompt: string;
  choices: string[];
  correctIndex: number | null;
  points: number;
  selectedIndex?: number | null;
  text?: string | null;
  awardedPoints: number;
  comment?: string | null;
};
export type AttemptDetail = {
  id: string;
  testId: string;
  courseId: string;
  userId: string;
  attemptNumber: number;
  entries: AttemptEntry[];
  autoScore: number;
  manualScore: number | null;
  maxScore: number;
  scorePct: number | null;
  status: 'pending_review' | 'passed' | 'failed';
  submittedAt: string;
};
export async function getPendingAttempt(id: string) {
  const res = await api.get<AttemptDetail>(`/creator/test-attempts/${id}`);
  return res.data;
}
export async function gradeAttempt(
  id: string,
  grades: { questionId: string; points: number; comment?: string | null }[],
) {
  const res = await api.post<{ attempt: AttemptDetail; certificateIssued: boolean }>(
    `/creator/test-attempts/${id}/grade`,
    { grades },
  );
  return res.data;
}

export type InboxPage = { items: InboxQuestion[]; nextOffset: number | null };
/** One page of the Q&A inbox. `unanswered` is filtered by the server across every question. */
export async function listInbox(opts: { unanswered: boolean; offset?: number }) {
  const res = await api.get<InboxPage>('/creator/questions', {
    params: { unanswered: opts.unanswered ? '1' : '0', offset: opts.offset ?? 0, limit: 50 },
  });
  return res.data;
}

export type ThreadReply = {
  id: string;
  body: string;
  isCreatorReply: boolean;
  hidden: boolean;
  createdAt: string;
  authorName: string | null;
};
/** The replies under one question, oldest first. */
export async function getQuestionThread(id: string) {
  const res = await api.get<{ replies: ThreadReply[] }>(`/creator/questions/${id}/thread`);
  return res.data.replies;
}
export async function replyToQuestion(id: string, body: string) {
  await api.post(`/creator/questions/${id}/reply`, { body });
}
export async function hideQuestion(id: string, hidden: boolean) {
  await api.post(`/creator/questions/${id}/hide`, { hidden });
}

// ── Notifications (LMS Phase 2) ─────────────────────────────────────────────
// `/me/notifications*` is `requireAuth` + `req.user.sub` only — a creator's
// access token reads that creator's own rows with no API change at all.
export type NotificationRow = {
  id: string;
  kind: string;
  title: string;
  body: string;
  metadata: Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
};

export type NotificationsPage = {
  items: NotificationRow[];
  nextCursor: string | null;
};

export async function listNotifications(args: {
  limit?: number;
  cursor?: string;
}): Promise<NotificationsPage> {
  const res = await api.get<NotificationsPage>('/me/notifications', { params: args });
  return res.data;
}

export async function getUnreadCount(): Promise<{ count: number }> {
  const res = await api.get<{ count: number }>('/me/notifications/unread-count');
  return res.data;
}

export async function markNotificationRead(id: string): Promise<{ id: string; readAt: string }> {
  const res = await api.post<{ id: string; readAt: string }>(`/me/notifications/${id}/read`);
  return res.data;
}

export async function markAllNotificationsRead(): Promise<{ marked: number }> {
  const res = await api.post<{ marked: number }>('/me/notifications/read-all');
  return res.data;
}

// ── LMS Phase 7: tenant flags, coupons, revenue, announcements ────────────

/**
 * Everything the shell needs from the PUBLIC tenant endpoint, in one call:
 * the feature flags plus the tenant's identity and theme.
 *
 * `subscriptionModel` gates the Subscription plan nav entry — under the tenant
 * model a creator has no plan of their own to sell, so the page would be a dead
 * end. `theme`/`name`/`logo` are what make Creator Studio a white-labelled
 * surface instead of the shared cyan baseline: the shell shows the tenant's
 * mark and name, and `useApplyTenantTheme` injects the tokens at boot.
 */
export async function getTenantFlags(): Promise<{
  lmsEnabled: boolean;
  messagingEnabled: boolean;
  subscriptionModel: 'tenant' | 'creator';
  tenantName: string;
  theme: ThemeTokens | null;
}> {
  const res = await api.get<{
    lmsEnabled?: boolean;
    messagingEnabled?: boolean;
    lmsSubscriptionModel?: 'tenant' | 'creator';
    name?: string;
    theme?: ThemeTokens;
  }>('/tenant/config');
  return {
    lmsEnabled: res.data.lmsEnabled === true,
    messagingEnabled: res.data.messagingEnabled === true,
    subscriptionModel: res.data.lmsSubscriptionModel === 'creator' ? 'creator' : 'tenant',
    tenantName: res.data.name ?? '',
    theme: res.data.theme ?? null,
  };
}

/**
 * The tenant's uploaded logo by SLOT. Each slot 404s when that key is unset —
 * there is no server-side fallback between them, so the caller picks the slot
 * from the theme keys it already has (see the Creator Studio rail, which wants
 * `logoKeyDark` and settles for `logoKey`).
 */
export function tenantLogoUrl(which: 'light' | 'dark' | 'favicon' = 'light') {
  return `${BASE_URL}/tenant/logo/${which}`;
}

/**
 * A creator's photo. Public and addressed by id, like every other
 * `<img>`-consumed asset route — a browser image request carries no
 * Authorization header, so a guarded one would simply never render.
 */
export function creatorAvatarUrl(userId: string) {
  return `${BASE_URL}/creators/${userId}/avatar`;
}

export type CreatorCoupon = {
  id: string;
  code: string;
  discountType: 'percent' | 'amount';
  discountValue: string;
  oneTime: boolean;
  maxUses: number | null;
  usedCount: number;
  perUserLimit: number | null;
  validFrom: string | null;
  validUntil: string | null;
  active: boolean;
  productIds: string[];
};

export type CreatorCouponInput = {
  code?: string;
  discountType: 'percent' | 'amount';
  discountValue: string;
  oneTime?: boolean;
  maxUses?: number | null;
  perUserLimit?: number | null;
  validFrom?: string | null;
  validUntil?: string | null;
  active?: boolean;
  productIds: string[];
};

export async function listCoupons(): Promise<CreatorCoupon[]> {
  return (await api.get<CreatorCoupon[]>('/creator/coupons')).data;
}
export async function createCoupon(input: CreatorCouponInput): Promise<CreatorCoupon> {
  return (await api.post<CreatorCoupon>('/creator/coupons', input)).data;
}
export async function updateCoupon(
  id: string,
  input: Partial<CreatorCouponInput>,
): Promise<CreatorCoupon> {
  return (await api.patch<CreatorCoupon>(`/creator/coupons/${id}`, input)).data;
}
export async function deleteCoupon(id: string): Promise<void> {
  await api.delete(`/creator/coupons/${id}`);
}

export type RevenueCourseRow = {
  courseId: string;
  title: string;
  units: number;
  coupon: string;
  /** Absent when the tenant hides the platform fee from this creator. */
  gross?: string;
  fee?: string;
  net: string;
  refunds: number;
  refundedGross: string;
};

export type CreatorRevenue = {
  showFee: boolean;
  from: string;
  to: string;
  totals: {
    net: string;
    gross?: string;
    fee?: string;
    orders: number;
    refunds: number;
    refundedNet: string;
  };
  series: { date: string; net: string; refunded: string }[];
  courses: RevenueCourseRow[];
  top: RevenueCourseRow[];
};

export async function getRevenue(params: { from?: string; to?: string } = {}) {
  return (await api.get<CreatorRevenue>('/creator/revenue', { params })).data;
}

/** CSV download. The bearer token lives in JS memory, so it cannot ride a
 *  plain `window.open` — fetch the blob and hand it to the browser. */
export async function downloadRevenueCsv(params: { from?: string; to?: string } = {}) {
  const res = await api.get('/creator/revenue', {
    params: { ...params, format: 'csv' },
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'creator-revenue.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export type CourseAnnouncement = {
  id: string;
  courseId: string;
  title: string;
  body: string;
  publishedAt: string | null;
  fannedOutAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export async function listAnnouncements(courseId: string): Promise<CourseAnnouncement[]> {
  return (await api.get<CourseAnnouncement[]>(`/creator/courses/${courseId}/announcements`)).data;
}
export async function createAnnouncement(
  courseId: string,
  input: { title: string; body: string; publish?: boolean },
): Promise<CourseAnnouncement & { notified: number }> {
  return (
    await api.post<CourseAnnouncement & { notified: number }>(
      `/creator/courses/${courseId}/announcements`,
      input,
    )
  ).data;
}
export async function updateAnnouncement(
  id: string,
  input: { title?: string; body?: string },
): Promise<CourseAnnouncement> {
  return (await api.patch<CourseAnnouncement>(`/creator/announcements/${id}`, input)).data;
}
export async function publishAnnouncement(
  id: string,
): Promise<{ published: boolean; notified: number }> {
  return (
    await api.post<{ published: boolean; notified: number }>(`/creator/announcements/${id}/publish`)
  ).data;
}
export async function deleteAnnouncement(id: string): Promise<void> {
  await api.delete(`/creator/announcements/${id}`);
}

// ── Subscription plans (LMS Phase 10) ───────────────────────────────────────

export type CreatorSubscriptionPlan = {
  id: string;
  period: 'monthly' | 'semi_annual' | 'annual';
  productId: string;
  price: string;
  active: boolean;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  rejectionReason: string | null;
  label: string;
};

export async function listSubscriptionPlans(): Promise<CreatorSubscriptionPlan[]> {
  const res = await api.get<{ plans: CreatorSubscriptionPlan[] }>('/creator/subscription-plans');
  return res.data.plans;
}

/**
 * The WHOLE set in one PUT, never a delta — a period the form omits is switched
 * off, which is how a creator withdraws one.
 */
export async function saveSubscriptionPlans(
  plans: Array<{ period: string; active: boolean; price: string }>,
): Promise<CreatorSubscriptionPlan[]> {
  const res = await api.put<{ plans: CreatorSubscriptionPlan[] }>('/creator/subscription-plans', {
    plans,
  });
  return res.data.plans;
}
